import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { CostService } from "src/app/shared/services/cost.service";
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
} from "@angular/forms";
import { tap, map, shareReplay, catchError, switchMap, filter } from "rxjs/operators";
import { EmployeeService } from "src/app/shared/services/employee.service";
import {
  CostAdditionalCodes,
  convertChecboxToCostAdditionalCodesArray,
  convertCostAdditionalCodesArrayToCheckbox,
} from "src/app/shared/models/cost-additional-code.model";
import { Cost, CostStatuses } from "src/app/shared/models/cost.model";
import { ApprovalPath } from "src/app/shared/models/approval-path.model";
import { ApprovalPathService } from "src/app/shared/services/approval-path.service";
import { CostActions } from "src/app/shared/models/cost-action.model";
import { convertDateToDatMonthNameYearObject } from "src/app/shared/utils/date.util";
import { Roles } from "src/app/shared/models/auth/role.model";
import { Employee } from "src/app/shared/models/employee.model";
import { PrintService } from "src/app/shared/services/print.service";
import { UserService } from "src/app/shared/services/user.service";
import { ToastrService } from "ngx-toastr";
import { CustomValidators } from "src/app/shared/directives/custom-validator.directive";
import { KSEF_NUMBER_PATTERN, formatKsefNumber } from "src/app/shared/utils/ksef.util";
import { Note } from "src/app/shared/models/note.model";
import { Attachment } from "src/app/shared/models/attachment.model";
import { AttachmentService } from "src/app/shared/services/attachment.service";
import { FileSaverService } from "ngx-filesaver";
import { ConfirmService } from "src/app/shared/services/confirm.service";
import { CostHistory } from "../../shared/models/cost-history.model";
import { forkJoin, Observable, throwError } from "rxjs";

type ControlState = "enable" | "disable";

@Component({
  selector: "app-cost",
  templateUrl: "./cost.component.html",
})
export class CostComponent implements OnInit {
  cost: Cost;
  notes = Note;
  console = console;

  approvalPath: ApprovalPath;
  costStatus = CostStatuses;

  isActionLoading = false;

  costForm: FormGroup;

  costId: number;
  currentUserId: number;
  currentUser: Employee;
  formattedDateUpdatedAt;
  formattedDateCreatedAt;
  formattedDateCreatedAtRegistred;
  originalInvoiceNameRequired$;
  employees: Employee[];

  idOfCurrentlySelectedEmployee = -1;
  idOfCurrentlySelectedCostType = -1;
  idOfCurrentlySelectedAdvance = -1;

  employees$;
  costTypesPossibleToRegister$;
  advances$;
  costAdditionalCodes = CostAdditionalCodes;
  hasCreatedThisCost;
  isConflictCase: boolean;
  isBlockedFromFormalApproval: boolean;
  authEmployeeRoles;

  attachments: Attachment[];
  costHistory: CostHistory[];

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private costService: CostService,
    private toastrService: ToastrService,
    private fb: FormBuilder,
    private employeeService: EmployeeService,
    private approvalRoutineService: ApprovalPathService,
    private printService: PrintService,
    private userService: UserService,
    private attachmentService: AttachmentService,
    private fs: FileSaverService,
    private confirmService: ConfirmService
  ) {}

  ngOnInit() {
    this.currentUser = this.userService.getCurrentUser();
    this.authEmployeeRoles = this.currentUser.user.roles;
    this.currentUserId = this.currentUser.id;
    this.idOfCurrentlySelectedEmployee = this.currentUserId;
    this.createFormGroup();
    this.setupOriginalInvoiceNameRequired();
    this.setupKsefNumberValidationAndFormat();
    this.costId = parseInt(
      this._activatedRoute.snapshot.paramMap.get("id"),
      10
    );

    if (!isNaN(this.costId)) {
      this.costService
        .getCost(this.costId)
        .pipe(tap((cost) => this.patchFormGroup(cost)))
        .subscribe((cost: Cost) => {
          this.filterEmployeesByCostType(-1);
          // this.filterCostTypesByEmployee(this.currentUserId);
          this.filterCostTypesByEmployee(cost.createdBy.id);

          this.cost = cost;
          this.costHistory = cost.costHistory;
          this.formattedDateCreatedAt = convertDateToDatMonthNameYearObject(
            this.cost.createdAt
          );
          if (this.cost.updatedAt) {
            this.formattedDateUpdatedAt = convertDateToDatMonthNameYearObject(
              this.cost.updatedAt
            );
          }
          if (cost.approvalPath) {
            this.approvalPath = ApprovalPath.adapt(cost.approvalPath);
            this.setDisableStatusOnFormGroup();
          }
          this.hasCreatedThisCost =
            this.cost.createdBy.id === this.currentUserId;
          this.isConflictCase = !!this.cost.isConflictCase;
          this.isBlockedFromFormalApproval =
            this.cost.blockedEmployeeId != null &&
            this.cost.blockedEmployeeId === this.currentUserId;
          this.fetchAttachments();
          // this.fetchHistory();
          // console.log(cost);
        });
    }
  }

  fetchAttachments() {
    this.costService.fetchAttachments(this.costId).subscribe((attachments) => {
      this.attachments = this.attachmentsSplitOriginalName(attachments);
    });
  }

  fetchHistory() {
    this.costService
      .fetchHistory(this.costId)
      .subscribe((costHistory: CostHistory[]) => {
        this.costHistory = costHistory;
        if (this.costHistory && this.costHistory.length > 0) {
          this.formattedDateCreatedAt = convertDateToDatMonthNameYearObject(
            this.costHistory[this.costHistory.length - 1].createdAt
          );
        }
      });
  }

  attachmentsSplitOriginalName(attachments: Attachment[]) {
    return attachments.map((attachment) => {
      const [filename, extension] = attachment.originalName.split(".");
      return {
        ...attachment,
        ...{
          filename: filename,
          extension: extension,
        },
      };
    });
  }

  patchFormGroup(cost) {
    this.idOfCurrentlySelectedCostType = cost.type.id;
    this.idOfCurrentlySelectedEmployee = cost.employee
      ? cost.employee.id
      : null;

    this.costForm.patchValue({
      ...cost,
      date: cost.docDate,
      additionalCodes: convertCostAdditionalCodesArrayToCheckbox(
        cost.additionalCodes
      ),
      employee: this.idOfCurrentlySelectedEmployee,
      type: this.idOfCurrentlySelectedCostType,
    });
  }

  /**
   * It fetches employees  based on selected cost type
   * @param costTypeId id of a cost type sent from a select control value or directly from typescript function call
   */
  filterEmployeesByCostType(costTypeId: string | number) {
    // costTypeId can be send either from typescript or template. Value from template (select control value) will be a string.
    const id =
      typeof costTypeId === "string" ? parseInt(costTypeId, 10) : costTypeId;

    // This is to keep track of curently selected cost type, when filterCostTypesByEmployee is called it will set this id as a select value
    this.idOfCurrentlySelectedCostType = id;

    if (costTypeId || !isNaN(id)) {
      this.changeControlEnableDisableState("employee", "disable");
      this.employees$ = this.employeeService.fetchEmployeesByCostType(id).pipe(
        tap((employees) => {
          // Checking if employee selected before fetching employees still exists
          const isIdOfCurrentlySelectedEmployeeStillExists = employees.some(
            (employee) => employee.id === this.idOfCurrentlySelectedEmployee
          );
          // if (!isIdOfCurrentlySelectedEmployeeStillExists) {
          //   this.filterAdvancesByEmployee(-1);
          // }
          this.changeControlEnableDisableState("employee", "enable");
          // this.costForm.patchValue({

          //   // If previously selected employee still exists set it as a value otherwise -1, which is a placeholder text
          //   employee: isIdOfCurrentlySelectedEmployeeStillExists ? this.idOfCurrentlySelectedEmployee : -1
          // });
        })
      );
    }
  }

  filterAdvancesByEmployee(employeeId: string | number) {
    // employeeId can be send either from typescript or template. Value from template (select control value) will be a string.
    const id =
      typeof employeeId === "string" ? parseInt(employeeId, 10) : employeeId;

    if (employeeId || !isNaN(id)) {
      this.changeControlEnableDisableState("advance", "disable");
      this.advances$ = this.employeeService.fetchAdvances(id).pipe(
        tap((v) => {
          const advanceId = v.length === 1 ? v[0].id : -1;

          this.changeControlEnableDisableState("advance", "enable");
          this.costForm.patchValue({
            advance: advanceId,
          });
        })
      );
    }
  }

  /**
   * It fetches cost types based on selected employee
   * @param employeeId id of an employee sent from a select control value or directly from typescript function call
   */
  filterCostTypesByEmployee(employeeId: string | number) {
    // employeeId can be send either from typescript or template. Value from template (select control value) will be a string.
    const id =
      typeof employeeId === "string" ? parseInt(employeeId, 10) : employeeId;
    this.changeControlEnableDisableState("type", "disable");
    // This is to keep track of curently selected employee, when filterEmployeesByCostType is called it will set this id as a select value
    this.idOfCurrentlySelectedEmployee = id;

    if (employeeId || !isNaN(id)) {
      this.costTypesPossibleToRegister$ = this.employeeService
        .fetchCostTypesPossibleToRegister(id)
        .pipe(
          map((costTypes) => costTypes.filter((v) => v.name !== "delegation")),
          tap((costTypes) => {
            // Checking if cost type selected before fetching new cost types still exists
            const isIdOfCurrentlySelectedCostTypeStillExists = costTypes.some(
              (costType) => costType.id === this.idOfCurrentlySelectedCostType
            );
            this.changeControlEnableDisableState("type", "enable");

            // this.costForm.patchValue({
            //   // If previously selected cost type still exists set it as a value otherwise -1, which is a placeholder text
            //   type: isIdOfCurrentlySelectedCostTypeStillExists ? this.idOfCurrentlySelectedCostType : -1
            // });
          })
        );
    }
  }

  createFormGroup() {
    this.costForm = this.fb.group({
      name: [null, [Validators.required]],
      advance: -1,
      grossValue: [null, [Validators.required]],
      docNumber: [
        null,
        [Validators.required, Validators.pattern(/^(?!\s).+(?<!\s)$/gm)],
      ],
      docDate: [null, [Validators.required]],
      isDocumentElectronic: [null, [Validators.required]],
      companyName: [],
      machineModel: [],
      customer: [],
      pono: [],
      delegationNumber: [],
      machineSerialNumber: [],
      detailedCode: [],
      description: [null, [Validators.required]],
      isReinvoicing: [],
      originalInvoiceName: [],
      additionalCodes: this.fb.group(
        {
          YmcePlExpense: false,
          Sale: false,
          Warehouse: false,
          Tests: false,
        },
        {
          validator: [CustomValidators.multipleCheckboxRequireOne],
        }
      ),
      type: [this.idOfCurrentlySelectedCostType, [Validators.required]],
      employee: [this.idOfCurrentlySelectedEmployee],
      note: "",
      isKsef: [null, Validators.required],
      ksefNumber: [null]
    });
  }

  setupOriginalInvoiceNameRequired() {
    this.originalInvoiceNameRequired$ = this.costForm
      .get("isReinvoicing")
      .valueChanges.pipe(
        map((value) => {
          /*
           * If IsReinvoicing is selected, no additional codes are required
           */
          if (value) {
            this.costForm
              .get("originalInvoiceName")
              .setValidators(Validators.required);
            this.costForm.get("additionalCodes").setValidators([]);
          } else {
            this.costForm
              .get("additionalCodes")
              .setValidators(CustomValidators.multipleCheckboxRequireOne);
            this.costForm.get("originalInvoiceName").setValidators([]);
          }

          this.costForm.get("additionalCodes").updateValueAndValidity();
          this.costForm.get("originalInvoiceName").updateValueAndValidity();

          return value;
        }),
        shareReplay()
      );
  }

  setupKsefNumberValidationAndFormat() {
    const isKsefCtrl = this.costForm.get("isKsef");
    const ksefNumberCtrl = this.costForm.get("ksefNumber");
    isKsefCtrl.valueChanges.pipe(
      tap((isKsef) => {
        if (isKsef) {
          ksefNumberCtrl.setValidators([
            Validators.required,
            Validators.pattern(KSEF_NUMBER_PATTERN),
          ]);
        } else {
          ksefNumberCtrl.setValidators(null);
          ksefNumberCtrl.setValue(null, { emitEvent: false });
        }
        ksefNumberCtrl.updateValueAndValidity();
      })
    ).subscribe();
    ksefNumberCtrl.valueChanges.pipe(
      filter(() => isKsefCtrl.value === true),
      map((raw) => formatKsefNumber(raw)),
      tap((formatted) => {
        if (ksefNumberCtrl.value !== formatted) {
          ksefNumberCtrl.setValue(formatted, { emitEvent: false });
        }
      })
    ).subscribe();
    if (isKsefCtrl.value) {
      ksefNumberCtrl.setValidators([
        Validators.required,
        Validators.pattern(KSEF_NUMBER_PATTERN),
      ]);
      ksefNumberCtrl.updateValueAndValidity();
    }
  }

  changeApprovalPathStatus(action: CostActions): Observable<any> {
    this.isActionLoading = true;
    return this.approvalRoutineService
      .changeApprovalPathStatus(
        this.costForm.get("note").value,
        this.approvalPath.id,
        action
      )
      .pipe(
        tap((response: any) => {
          this.updateCost(response);
          this.toastrService.success("Poprawnie zmieniono status");
          this.isActionLoading = false;
        }),
        catchError((error) => {
          this.isActionLoading = false;
          return throwError(error);
        })
      );
  }

  changeCostStatus(action: CostActions): Observable<any> {
    this.isActionLoading = true;
    return this.costService
      .changeCostStatus(this.cost.id, action, [new Cost(this.cost.id)])
      .pipe(
        tap((response: any) => {
          this.updateCost(response[0]);
          this.toastrService.success("Poprawnie zmieniono status");
          this.isActionLoading = false;
        }),
        catchError((error) => {
          this.isActionLoading = false;
          return throwError(error);
        })
      );
  }

  sendForApproval(): Observable<any> {
    if (this.attachments.length <= 0) {
      this.toastrService.error(
        "Aby przekazać koszt do akceptacji musisz dodać pliki"
      );
      return throwError("No attachments added.");
    }

    this.isActionLoading = true;
    return this.costService.sendForApproval(this.cost.id).pipe(
      tap((response: any) => {
        this.updateCost(response);
        this.setDisableStatusOnFormGroup();
        this.toastrService.success("Poprawnie wysłano koszt do akceptacji");
        this.isActionLoading = false;
      }),
      catchError((error) => {
        this.isActionLoading = false;
        return throwError(error);
      })
    );
  }

  printCost(): Observable<any> {
    this.isActionLoading = true;
    return this.costService
      .changeCostStatus(this.cost.id, CostActions.Print, [
        new Cost(this.cost.id),
      ])
      .pipe(
        tap((response: Cost[]) => {
          this.printService.addCosts(response);
          this._router.navigate(["print/costs"]);
          this.isActionLoading = false;
        }),
        catchError((error) => {
          this.isActionLoading = false;
          return throwError(error);
        })
      );
  }

  exportCost(): Observable<any> {
    this.isActionLoading = true;
    const filename = Date.now() + ".csv";
    this.toastrService.success(
      "Za moment uruchomi się pobieranie pliku do przelewu."
    );

    return this.costService.exportToCsv([new Cost(this.cost.id)]).pipe(
      tap((result) => {
        this.fs.save(result, filename);
        this.isActionLoading = false;
      }),
      catchError((error) => {
        this.toastrService.error(
          "Wystąpił problem z wygenerowaniem pliku przelewu."
        );
        this.isActionLoading = false;
        return throwError(error);
      })
    );
  }

  cancelCost(): Observable<any> {
    this.isActionLoading = true;

    return this.confirmService
      .openConfirmDialog("Czy na pewno chcesz anulować koszt?")
      .afterClosed()
      .pipe(
        switchMap((confirm) => {
          if (confirm) {
            return this.costService
              .changeCostStatus(this.cost.id, CostActions.Cancel, [
                new Cost(this.cost.id),
              ])
              .pipe(
                tap((response: any) => {
                  this.updateCost(response[0]);
                  this.toastrService.success("Poprawnie anulowano koszt");
                  this.isActionLoading = false;
                })
              );
          } else {
            this.isActionLoading = false;
            return "EMPTY";
          }
        }),
        catchError((error) => {
          this.isActionLoading = false;
          return throwError(error);
        })
      );
  }

  prepareCost() {
    const transformedForm = {
      ...this.costForm.value,
      additionalCodes: convertChecboxToCostAdditionalCodesArray(
        this.costForm.value.additionalCodes
      ),
    };
    const cost = Cost.adapt(transformedForm);
    if (typeof cost.docNumber === "string") {
      cost.docNumber = cost.docNumber.trim();
    }
    cost.updatedAt = this.cost.updatedAt;
    return cost;
  }

  correctCost(): Observable<any> {
    this.isActionLoading = true;
    const cost = this.prepareCost();

    return this.costService.updateCost(cost, this.costId).pipe(
      tap((response: any) => {
        this.updateCost(response);
        this.cost = Cost.adapt(response);
        this.patchFormGroup(this.cost);
        this.toastrService.success("Poprawnie zaktualizowano koszt.");
        this.isActionLoading = false;
      }),
      catchError((error) => {
        this.toastrService.error("Zmiany nie zostały zapisane.");
        this.isActionLoading = false;
        return throwError(error);
      })
    );
  }

  saveCost(): Observable<any> {
    this.isActionLoading = true;
    const cost = this.prepareCost();
    return this.costService.updateCost(cost, this.costId).pipe(
      catchError((error) => {
        this.setLoading(false);
        return throwError(error);
      })
    );
  }

  updateCost(response) {
    this.cost.canCurrentUserMakeAction = response.canCurrentUserMakeAction;
    if (response.isConflictCase !== undefined) {
      this.cost.isConflictCase = response.isConflictCase;
      this.isConflictCase = response.isConflictCase;
    }
    if (response.blockedEmployeeId !== undefined) {
      this.cost.blockedEmployeeId = response.blockedEmployeeId;
      this.isBlockedFromFormalApproval =
        response.blockedEmployeeId != null &&
        response.blockedEmployeeId === this.currentUserId;
    }
    this.approvalPath = response.approvalPath
      ? ApprovalPath.adapt(response.approvalPath)
      : null;
    this.cost.status = response.status;
    this.setDisableStatusOnFormGroup();
    this.fetchHistory();
    // this.cost.notes = response.notes;
  }

  setDisableStatusOnFormGroup() {
    if (!this.shouldBeDisabledBasedOnCostStatus()) {
      this.enableFormGroup();
    } else {
      this.disableFormGroup();
    }
  }

  disableFormGroup() {
    this.costForm.disable();
    // Object.keys(this.costForm.controls).forEach(key => {
    //   this.costForm.get(key).disable();
    // });
  }

  enableFormGroup() {
    this.costForm.enable();
    // Object.keys(this.costForm.controls).forEach(key => {
    //   this.costForm.get(key).enable();
    // });
  }

  refreshNotes(notes: Note[]) {
    this.cost.notes = notes;
  }

  handleFileInput(event) {
    if (event.target.files && event.target.files.length > 0) {
      const files = event.target.files;
      this.attachmentService
        .uploadAttachment(files, this.costId)
        .subscribe((response) => {
          // this.redirectTo('/cost/' + this.costId);
          this.attachments = this.attachmentsSplitOriginalName(response);
          this.toastrService.success("Operacja przebiegła pomyślnie.");
        });
    } else {
      this.toastrService.error("Wystąpił błąd podczas wykonywania operacji.");
    }
  }

  // TODO: Change redirect to response!
  redirectTo(uri: string) {
    this._router
      .navigateByUrl("/", { skipLocationChange: true })
      .then(() => this._router.navigate([uri]));
  }

  removeAttachment(id) {
    this.attachmentService.removeAttachment(id).subscribe(
      (_) => {
        this.attachments = this.attachments.filter((v) => v.id !== id);
        this.toastrService.success("Plik został usunięty.");
      },
      (_) => this.toastrService.error("Nie można usunąć załącznika.")
    );
  }

  downloadAttachment(id, name) {
    this.attachmentService.downloadAttachment(id).subscribe((result) => {
      this.fs.save(result, name);
    });
  }

  private changeControlEnableDisableState(
    controlName: string,
    state: ControlState
  ) {
    if (!this.shouldBeDisabledBasedOnCostStatus()) {
      this.setControlEnableDisableState(controlName, state);
    }
  }

  private setControlEnableDisableState(
    controlName: string,
    state: ControlState
  ) {
    switch (state) {
      case "enable":
        this.costForm.get(controlName).enable();
        break;
      case "disable":
        this.costForm.get(controlName).disable();
        break;
    }
  }

  shouldBeDisabledBasedOnCostStatus() {
    // * Inputs are not disabled on status 'New'
    if (this.cost) {
      return (
        ![
          CostStatuses.New,
          CostStatuses.InFormalApproval,
          CostStatuses.InMeritoricalApproval,
          CostStatuses.Rejected,
        ].includes(this.cost.status.name) || !this.cost.canCurrentUserMakeAction
      );
    } else {
      return false;
    }
  }

  cannotMakeAction() {
    return (
      (this.cost.createdBy.id === this.currentUserId &&
        !this.currentUser.user.roles.includes(Roles.Manager) &&
        !this.currentUser.user.roles.includes(Roles.Admin) &&
        !this.currentUser.user.roles.includes(Roles.Staff)) ||
      !this.cost.canCurrentUserMakeAction
    );
  }

  handleAction(action) {
    this.setLoading(true);

    let operations: Observable<any>[] = [];

    switch (action) {
      case "approve":
      case "reject":
        operations.push(this.changeApprovalPathStatus(action));
        operations.push(this.saveCost());
        break;
      case "update":
        operations.push(this.correctCost());
        break;
      case "cancel":
        operations.push(this.cancelCost());
        break;
      case "print":
        operations.push(this.printCost());
        break;
      case "sendForApproval":
        operations.push(this.saveCost());
        operations.push(this.sendForApproval());
        break;
      case "export":
        if (CostStatuses.Printed === this.cost.status.name) {
          operations.push(this.changeCostStatus(action));
        }
        operations.push(this.exportCost());
        break;
      case "return":
      case "markAsReturned":
        operations.push(this.changeCostStatus(action));
        break;
      default:
        this.toastrService.error(`Unrecognized action: ${action}`);
        this.setLoading(false);
        return;
    }

    if (operations.length > 0) {
      forkJoin(operations).subscribe({
        next: (results) => {
          this.setLoading(false);
        },
        error: (error) => {
          this.setLoading(false);
        },
      });
    } else {
      this.setLoading(false);
    }
  }

  setLoading(isLoading: boolean) {
    this.isActionLoading = isLoading;
  }
}
